import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Default Leaflet marker icons break under CRA bundling; use CDN assets.
const markerIcon = L.icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const FALLBACK = { lat: 40.7128, lng: -74.006 };

/**
 * Small OSM map with a draggable pin. Calls onChange({ lat, lng }) on drag end.
 */
export default function LocationPinMap({
  latitude,
  longitude,
  onChange,
  height = 220,
  testId = "location-pin-map",
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const lat = latitude != null ? Number(latitude) : FALLBACK.lat;
  const lng = longitude != null ? Number(longitude) : FALLBACK.lng;

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return undefined;
    const map = L.map(containerRef.current, {
      center: [lat, lng],
      zoom: latitude != null ? 14 : 11,
      scrollWheelZoom: false,
    });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>',
      maxZoom: 19,
    }).addTo(map);
    const marker = L.marker([lat, lng], { draggable: true, icon: markerIcon }).addTo(map);
    marker.on("dragend", () => {
      const p = marker.getLatLng();
      onChangeRef.current?.({ lat: p.lat, lng: p.lng });
    });
    mapRef.current = map;
    markerRef.current = marker;
    // Leaflet needs a tick after mount to size correctly in flex layouts.
    setTimeout(() => map.invalidateSize(), 50);
    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
    // intentionally mount-once; position sync is below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!mapRef.current || !markerRef.current) return;
    if (latitude == null || longitude == null) return;
    const next = L.latLng(Number(latitude), Number(longitude));
    const cur = markerRef.current.getLatLng();
    if (Math.abs(cur.lat - next.lat) < 1e-6 && Math.abs(cur.lng - next.lng) < 1e-6) return;
    markerRef.current.setLatLng(next);
    mapRef.current.setView(next, mapRef.current.getZoom());
  }, [latitude, longitude]);

  return (
    <div
      ref={containerRef}
      data-testid={testId}
      className="z-0 w-full overflow-hidden rounded-[12px] border border-border"
      style={{ height }}
    />
  );
}
